// Global "Moviments i tiquets" panel (money users, no unitat open):
// unitat/estat/cap filters + ZIP download + the filtered entry list.
// Every mutation refreshes this via loadSummary → loadMoviments.

import { useFactures } from './useFacturesContext';
import { axiosInstance as axios } from '../../config/axios';
import { FACTURES_UNITATS } from '../../constants/roles';
import { dateOf, outstanding, zipFilterFromEstat } from './facturesTypes';
import { extractErrorMessage } from '../../utils/errorHandling';
import { EntryItem } from './EntryItem';
import {
	FormRow,
	Row,
	NoteText,
	Panel,
	SubTitle,
	YearSelect,
} from './Factures.styles';
import { Button, FormGroup, Label } from '../Shared/FormStyles';

export const MovimentsPanel = () => {
	const fac = useFactures();

	const downloadZip = async () => {
		fac.setZipBusy(true);
		try {
			const res = await axios.get(
				`/api/finances/moviments/${fac.year}/zip`,
				{
					responseType: 'blob',
					params: { filter: zipFilterFromEstat(fac.movEstat) },
				}
			);
			const a = document.createElement('a');
			const objUrl = URL.createObjectURL(
				new Blob([res.data], { type: 'application/zip' })
			);
			a.href = objUrl;
			a.download = `tiquets-${fac.year}-${zipFilterFromEstat(fac.movEstat)}.zip`;
			a.click();
			URL.revokeObjectURL(objUrl);
		} catch (e) {
			fac.setError(extractErrorMessage(e as Error));
		} finally {
			fac.setZipBusy(false);
		}
	};

	const shown = (fac.moviments ?? [])
		.filter((e) => {
			const norm = e.capName?.toLowerCase().replace(/\s+/g, ' ') ?? '';
			if (fac.movUnitat !== 'tots' && e.unitat !== fac.movUnitat) {
				return false;
			}
			if (
				fac.movCap !== 'tots' &&
				norm !== fac.movCap.toLowerCase().replace(/\s+/g, ' ')
			) {
				return false;
			}
			const out = outstanding(e);
			if (fac.movEstat === 'pendents') {
				return e.type === 'despesa' && out !== 0;
			}
			if (fac.movEstat === 'reemborsats') {
				return e.type === 'despesa' && out === 0;
			}
			return true;
		})
		.sort((a, b) => {
			const da = dateOf(a);
			const db = dateOf(b);
			return da < db ? 1 : da > db ? -1 : 0;
		});

	return (
		<Panel style={{ marginTop: '1.5rem' }}>
			<SubTitle style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
				Moviments i tiquets
			</SubTitle>
			<Row>
				<FormGroup style={{ flex: '0 0 auto' }}>
					<Label>Unitat:</Label>
					<YearSelect
						value={fac.movUnitat}
						onChange={(e) => fac.setMovUnitat(e.target.value)}
					>
						<option value="tots">Totes</option>
						{FACTURES_UNITATS.map((u) => (
							<option key={u.id} value={u.id}>
								{u.nom}
							</option>
						))}
					</YearSelect>
				</FormGroup>
				<FormGroup style={{ flex: '0 0 auto' }}>
					<Label>Estat:</Label>
					<YearSelect
						value={fac.movEstat}
						onChange={(e) =>
							fac.setMovEstat(
								e.target.value as typeof fac.movEstat
							)
						}
					>
						<option value="tots">Tots</option>
						<option value="pendents">Pendents</option>
						<option value="reemborsats">Reemborsats</option>
					</YearSelect>
				</FormGroup>
				<FormGroup style={{ flex: '0 0 auto' }}>
					<Label>Cap:</Label>
					<YearSelect
						value={fac.movCap}
						onChange={(e) => fac.setMovCap(e.target.value)}
					>
						<option value="tots">Tots</option>
						{fac.capNames.map((n) => (
							<option key={n} value={n}>
								{n}
							</option>
						))}
					</YearSelect>
				</FormGroup>
				<FormRow>
					<FormGroup style={{ flex: '0 0 auto' }}>
						<Label style={{ visibility: 'hidden' }}>.</Label>
						<Button onClick={downloadZip} disabled={fac.zipBusy}>
							{fac.zipBusy
								? 'Generant ZIP…'
								: "Descarrega't tiquets (ZIP)"}
						</Button>
					</FormGroup>
				</FormRow>
			</Row>
			{shown.length === 0 && (
				<NoteText>Cap moviment amb aquests filtres.</NoteText>
			)}
			<div>
				{shown.map((e) => (
					<EntryItem
						key={e._id}
						entry={e}
						saldo={null}
						showUnitat={fac.movUnitat === 'tots'}
					/>
				))}
			</div>
		</Panel>
	);
};
